import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { JOIN_GROUP_MUTATION, GROUPS_QUERY } from '../graphql';
import GroupCover from './GroupCover';

const CATEGORY_COLORS = {
  'Programming':'#2563EB','Art & Design':'#C026D3','Music':'#EA580C',
  'Gaming':'#0891B2','Science':'#0284C7','Writing':'#65A30D',
  'Business':'#CA8A04','Sports':'#16A34A','Language':'#7C3AED',
  'Cooking':'#C05621','Gardening':'#15803D','Reading':'#9333EA',
  'Dance':'#E11D48','Crafts':'#D97706','Photography':'#0369A1',
  'Outdoor Activity':'#166534','Other':'#78716C',
};
const DAY_SHORT = { Monday:'Mon',Tuesday:'Tue',Wednesday:'Wed',Thursday:'Thu',Friday:'Fri',Saturday:'Sat',Sunday:'Sun' };

// Clean, Meetup-style group card: cover image, name, "City, Country · N members", a subtle
// category chip, and a Join action.
export default function GroupCard({ group, onAuthRequired }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [joinGroup, { loading }] = useMutation(JOIN_GROUP_MUTATION, { refetchQueries: [{ query: GROUPS_QUERY }] });

  const catColor = CATEGORY_COLORS[group.category] || 'var(--primary)';
  const loc = [group.location?.city, group.location?.country].filter(Boolean).join(', ')
    || group.location?.building || group.location?.neighborhood || '';
  const kids = currentUser?.ageGroup === 'KIDS';

  const handleJoin = async (e) => {
    e.stopPropagation();
    if (!currentUser) { onAuthRequired?.(); return; }
    try { await joinGroup({ variables: { groupId: group.id } }); navigate(`/group/${group.id}`); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="card group-card" onClick={() => navigate(`/group/${group.id}`)}
      style={{ cursor:'pointer', padding:0, overflow:'hidden', display:'flex', flexDirection:'column', borderLeft:`3px solid ${catColor}` }}>

      <div style={{ position:'relative' }}>
        <GroupCover group={group} height={120} radius="0" />
        {group.isMember && <span className="badge" style={{ position:'absolute', top:8, right:8, background:'var(--success)', color:'#fff', fontSize:11 }}>✓ Joined</span>}
        {!group.isOpen && !group.isMember && <span className="badge" style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:11 }}>Full</span>}
      </div>

      <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:5, flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:catColor, flexShrink:0 }} />
          <span style={{ fontSize:11, fontWeight:700, color:catColor, textTransform:'uppercase', letterSpacing:'0.5px' }}>{group.category}</span>
        </div>
        <h3 style={{ fontSize:'var(--font-base)', fontWeight:800, lineHeight:1.3, margin:0 }}>{group.name}</h3>
        {loc && <div style={{ fontSize:12, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:4 }}>
          <span>📍</span><span>{loc}</span>
        </div>}
        {group.description && (
          <p style={{ fontSize:'var(--font-sm)', color:'var(--text-muted)', lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', margin:0 }}>
            {group.description}
          </p>
        )}

        <div style={{ marginTop:'auto', paddingTop:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:12, color:'var(--text-dim)' }}>👥 {group.memberCount} member{group.memberCount === 1 ? '' : 's'}</span>
          {group.isMember ? (
            <button className="btn btn-ghost btn-sm" onClick={e=>{e.stopPropagation();navigate(`/group/${group.id}`);}} style={{ fontSize:12, padding:'5px 10px' }}>Open →</button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={handleJoin} disabled={loading || !group.isOpen} style={{ fontSize:12, padding:'5px 12px' }}>
              {loading ? '…' : group.isOpen ? 'Join' : 'Full'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
